/**
 * A wrapper to handle the Modern pagination style, and infinite scroll.
 *
 * Usage:
 *  {%- if settings.pagination_style == "modern" or settings.pagination_infinite -%}
 *    <script src="{{ 'custom-pagination.js' | asset_url }}" defer="defer"></script>
 *  {%- endif -%}
 *
 *  {%- paginate thing by stuff -%}
 *    <custom-pagination data-infinite-scroll="{{ settings.pagination_infinite }}"
 *      data-pause-infinite-scroll="false">
 *      <ul>
 *        <li class="js-pagination-result">One</li>
 *        <li class="js-pagination-result">Two</li>
 *        <li class="js-pagination-result">Three</li>
 *      </ul>
 *      {%- if paginate.pages > 1 -%}
 *        {% render 'pagination', paginate: paginate, style: settings.pagination_style %}
 *      {%- endif -%}
 *    </custom-pagination>
 *  {%- endpaginate -%}
 */
if (!customElements.get('custom-pagination')) {
  /**
   * Regios Discounts (DOPP): re-run discounts on infinite-scroll / load-more rows.
   * @see https://regiostech.crisp.help/en/article/dopp-api-examples-1p830j3/
   * @param {string} newResultsHtml - Appended markup containing .js-pagination-result > product-card
   */
  function regiosNewItemsFromHtml(newResultsHtml) {
    if (!newResultsHtml) return [];
    const wrap = document.createElement('div');
    wrap.innerHTML = newResultsHtml;
    const items = [];
    wrap.querySelectorAll('product-card[data-product-id][data-variant-id][data-regular-price-cents]').forEach((card) => {
      const productId = parseInt(card.getAttribute('data-product-id'), 10);
      const variantId = parseInt(card.getAttribute('data-variant-id'), 10);
      const regularPriceInCents = parseInt(card.getAttribute('data-regular-price-cents'), 10);
      if (
        Number.isInteger(productId) &&
        productId > 0 &&
        Number.isInteger(variantId) &&
        variantId > 0 &&
        Number.isInteger(regularPriceInCents) &&
        regularPriceInCents >= 0
      ) {
        items.push({ productId, variantId, regularPriceInCents });
      }
    });
    return items;
  }

  class CustomPagination extends HTMLElement {
    constructor() {
      super();
      this.init();
      document.addEventListener('on:bfcache:load-restore', this.reload.bind(this));
    }

    disconnectedCallback() {
      this.destroy();
    }

    init() {
      this.infiniteScroll = this.dataset.infiniteScroll === 'true';
      this.pagination = this.querySelector('.js-pagination');
      this.loadMoreButton = this.querySelector('.js-pagination-load-more');
      this.results = this.querySelectorAll('.js-pagination-result');
      this.controller = null;

      // Bind click handlers to pagination results
      this.resultClickHandler = this.resultClickHandler || this.handleResultClick.bind(this);
      this.addEventListener('click', this.resultClickHandler);

      // Bind click handler to load more button click
      this.loadMoreClickHandler = this.loadMoreClickHandler || this.handleLoadMoreClick.bind(this);

      if (this.pagination && this.pagination.dataset.isMoreResults === 'true' && this.results) {
        if (this.loadMoreButton) this.loadMoreButton.addEventListener('click', this.loadMoreClickHandler);

        if (this.infiniteScroll) {
          this.paginationObserver = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting) {
                  this.loadMore();

                  // Continue to load results as long as the 'load more' button is visible
                  this.intersectingTimer = setInterval(this.loadMore.bind(this), 1000);
                } else if (this.intersectingTimer) {
                  clearInterval(this.intersectingTimer);
                }
              });
            },
            { rootMargin: `${window.innerHeight * 1.5}px 0px` }
          );

          this.paginationObserver.observe(this.pagination);
        }
      }
    }

    /**
     * Destroys the component
     */
    destroy() {
      if (this.intersectingTimer) clearInterval(this.intersectingTimer);
      if (this.paginationObserver) this.paginationObserver.disconnect();
      if (this.loadMoreButton) this.loadMoreButton.removeEventListener('click', this.loadMoreClickHandler);
      this.removeEventListener('click', this.resultClickHandler);
      this.loadMoreClickHandler = null;
    }

    /**
     * Reloads the component
     */
    reload() {
      this.destroy();
      this.init();
    }

    /**
     * Handles click on the load more button
     * @param {object} evt - Event object
     */
    handleLoadMoreClick(evt) {
      evt.preventDefault();
      this.loadMore();
    }

    /**
     * Handles a click on a item in the pagination
     * @param {object} evt - Event object
     */
    handleResultClick(evt) {
      if (evt.target.closest('a') && evt.target.closest('.js-pagination-result') && this.controller) {
        this.controller.abort();
      }
    }

    /**
     * Fetches more results from the next page of results, and dynamically updates this page
     */
    async loadMore() {
      if (!this.isFetching && this.dataset.pauseInfiniteScroll === 'false') {
        this.isFetching = true;
        this.controller = new AbortController();

        const paginationMessageEl = this.pagination?.querySelector('.js-pagination-message');
        const paginationMessagePrev = paginationMessageEl ? paginationMessageEl.textContent : '';
        if (paginationMessageEl) {
          paginationMessageEl.textContent = 'Loading Products';
        }

        try {
          // Add the loading state to the button
          if (!this.loadMoreButton) throw new Error("No 'Load more' button found.");

          this.loadMoreButton.classList.add('is-loading');
          this.loadMoreButton.setAttribute('aria-disabled', 'true');

          if (this.pagination?.dataset?.paginationStyle === 'traditional') {
            this.pagination.classList.add('is-loading');
          }

          // Retrieve and parse the next page
          const response = await fetch(this.loadMoreButton.href, {
            signal: this.controller.signal
          });

          if (!response.ok) throw new Error(response.status);
          if (this.dataset.pauseInfiniteScroll === 'true') {
            throw new Error('Infinite scroll was interrupted.');
          }

          const tmpl = document.createElement('template');
          tmpl.innerHTML = await response.text();

          // Append the additional results
          let newResultsHtml = '';
          const lastResult = this.results[this.results.length - 1];
          tmpl.content.querySelectorAll('.js-pagination-result').forEach((result) => {
            newResultsHtml += result.outerHTML;
          });
          lastResult.insertAdjacentHTML('afterend', newResultsHtml);
          window.initLazyImages();
          this.results = this.querySelectorAll('.js-pagination-result');

          CustomPagination.notifyRegiosDoppNewItems(newResultsHtml);

          // Update the pagination content
          const newPagination = tmpl.content.querySelector('.js-pagination');
          // data-is-more-results is always a string; "false" is truthy in JS — compare explicitly
          const hasMorePages = newPagination?.dataset?.isMoreResults === 'true';

          if (hasMorePages) {
            this.pagination.innerHTML = newPagination.innerHTML;

            // Rebind to the new load more button
            if (this.loadMoreButton) {
              this.loadMoreButton.removeEventListener('click', this.loadMoreClickHandler);
              this.loadMoreButton = this.querySelector('.js-pagination-load-more');
              if (this.loadMoreButton) {
                this.loadMoreButton.addEventListener('click', this.loadMoreClickHandler);
              }
            }
          } else {
            this.pagination.remove();
            this.pagination = null;
            if (this.intersectingTimer) clearInterval(this.intersectingTimer);
            if (this.paginationObserver) this.paginationObserver.disconnect();
            document.querySelectorAll('.js-when-paginated-only').forEach((elem) => elem.remove());
          }
        } catch (error) {
          console.log('Unable to fetch next page of results', error); // eslint-disable-line
          if (this.intersectingTimer) clearInterval(this.intersectingTimer);
          if (paginationMessageEl && paginationMessageEl.isConnected && paginationMessagePrev) {
            paginationMessageEl.textContent = paginationMessagePrev;
          }
        } finally {
          this.controller = null;

          // Remove the loading state from the load more button
          if (this.loadMoreButton) {
            this.loadMoreButton.classList.remove('is-loading');
            this.loadMoreButton.removeAttribute('aria-disabled');
          }

          this.isFetching = false;

          if (this.pagination?.dataset?.paginationStyle === 'traditional') {
            this.pagination.classList.remove('is-loading');
          }
        }
      }
    }

    /**
     * @param {string} newResultsHtml
     */
    static notifyRegiosDoppNewItems(newResultsHtml) {
      queueMicrotask(() => {
        try {
          const newItems = regiosNewItemsFromHtml(newResultsHtml);
          if (!newItems.length) return;
          window.dispatchEvent(
            new CustomEvent('regios-dopp:collection-page:new-items', {
              bubbles: true,
              detail: { uniqueKey: 'default', newItems }
            })
          );
        } catch (e) {
          /* Regios optional */
        }
      });
    }
  }

  customElements.define('custom-pagination', CustomPagination);
}
